<template>
  <section class="d-block adviser-carousel-container">
    <div class="d-flex w-100 align-items-center">
      <template v-if="isMobile">
        <div class="carousel-top-text">
          <span v-if="!selectedAdviser">
            {{ availableAdvisersTitle }}
          </span>
          <TransitionGroup>
            <div v-if="selectedAdviser">
              <Transition name="fade">
                <span>
                  {{ $t('appointment_form_page.available_advisers.carousel.selected') }} <br />
                </span>
              </Transition>
              <Transition name="fade" mode="out-in">
                <span :key="selectedAdviser.id" class="fw-bold">
                  {{ selectedAdviser.first_name }} {{ selectedAdviser.last_name }}
                </span>
              </Transition>
            </div>
          </TransitionGroup>
        </div>
      </template>
      <template v-else>
        <div class="carousel-top-text">
          {{ availableAdvisersTitle }}
          <TransitionGroup>
            <div v-if="selectedAdviser">
              <Transition name="fade">
                <span>
                  &nbsp;|&nbsp;{{
                    $t('appointment_form_page.available_advisers.carousel.selected')
                  }}&nbsp;
                </span>
              </Transition>
              <Transition name="fade" mode="out-in">
                <span :key="selectedAdviser.id" class="fw-bold">
                  {{ selectedAdviser.first_name }} {{ selectedAdviser.last_name }}
                </span>
              </Transition>
            </div>
          </TransitionGroup>
        </div>
      </template>
    </div>
    <div v-if="hasSwiperEnabled" class="carousel-wrapper">
      <swiper-container
        ref="swiperRef"
        class="adviser-cards"
        :modules="swiperModules"
        :pagination="{
          el: '.pagination-container',
          type: 'bullets',
          clickable: true
        }"
        :class="selectedAdviser ? 'adviser-cards--has-selection' : null"
        :grid="{
          rows: 1
        }"
        :breakpoints="{
          200: {
            slidesPerView: showPagination ? 2.2 : 3,
            spaceBetween: 5,
            loop: true
          },
          700: {
            slidesPerView: showPagination ? 3.2 : 3,
            spaceBetween: 7,
            loop: true
          },
          1200: {
            spaceBetween: 10,
            loop: true,
            slidesPerView: 3
          }
        }"
        navigation-prev-el=".button-prev"
        navigation-next-el=".button-next"
      >
        <AdviserCard
          v-for="(adviser, index) in mappedAdvisers"
          :key="adviser.id"
          :adviser="adviser"
          :lang="lang"
          :is-selected="adviser.id === selectedAdviser?.id"
          :image="getImage(adviser.id)"
          :show-images="showImages"
          :current-image-index-to-load="currentImageIndexToLoad"
          :show-spoken-languages="showAdviserSpokenLanguages"
          :show-biography="showAdviserBiography"
          tag="swiper-slide"
          @select="selectAdviser(adviser, index)"
          @load-next="loadNext()"
          @is-ready="toggleReady(adviser.id)"
        />
      </swiper-container>
      <CarouselNavAndPagination :has-swiper-enabled="showPagination" />
    </div>
    <div
      v-else
      :class="selectedAdviser ? 'adviser-cards--has-selection' : null"
      class="adviser-cards adviser-cards--small-count"
      :data-count="advisersCount"
    >
      <AdviserCard
        v-for="adviser in mappedAdvisers"
        :key="adviser.id"
        :adviser="adviser"
        :lang="lang"
        tag="div"
        :is-selected="adviser.id === selectedAdviser?.id"
        :image="getImage(adviser.id)"
        :show-images="showImages"
        :current-image-index-to-load="currentImageIndexToLoad"
        :show-spoken-languages="showAdviserSpokenLanguages"
        :show-biography="showAdviserBiography"
        @select="selectAdviser(adviser)"
        @load-next="loadNext()"
        @is-ready="toggleReady(adviser.id)"
      />
    </div>
  </section>
</template>

<script lang="ts">
import { defineComponent, ref, PropType } from 'vue'
import { Adviser } from '~concierge/types/AppointmentTypes'
import { wTransChoice } from 'laravel-vue-i18n'
import { register } from 'swiper/element/bundle'
import { Swiper } from 'swiper/types'
import 'swiper/css'
import 'swiper/css/grid'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import { Pagination } from 'swiper/modules'

import AdviserCard from '~conciergeUiCommons/components/AppointmentQuestionComponents/AdviserCard.vue'
import { useShowImage } from '~conciergeUiCommons/composables/showImage'
import { useMobileDetection } from '~conciergeUiCommons/composables/deviceDetection'
import { useIdentityStore } from '~conciergeUiCommons/store/identityStore'
import { useSubmissionFormStore } from '~conciergeUiCommons/store/submissionFormStore'
import CarouselNavAndPagination from '~conciergeUiCommons/components/AppointmentQuestionComponents/CarouselNavAndPagination.vue'
import { Swiper as SwiperContainer } from 'swiper/vue'

register()

export default defineComponent({
  name: 'AdviserCarouselContainer',
  components: {
    AdviserCard,
    CarouselNavAndPagination
  },
  emits: ['selectAdviser', 'changeAdviser'],
  setup(props) {
    const {
      toggleReady,
      getImage,
      loadNext,
      showImages,
      imagesPayload,
      currentImageIndexToLoad,
      firstImagePayloadBatchCount
    } = useShowImage(props.advisers.length)
    const swiperRef = ref<typeof SwiperContainer | null>(null)
    const { isMobile } = useMobileDetection()
    const identityStore = useIdentityStore()
    const showAdviserBiography = identityStore.identity?.settings?.adviserBiographyEnabled ?? false
    const showAdviserSpokenLanguages =
      identityStore.identity?.settings?.adviserSpokenLanguageEnabled ?? false

    return {
      showAdviserSpokenLanguages,
      showAdviserBiography,
      isMobile,
      showImages,
      toggleReady,
      swiperRef,
      getImage,
      loadNext,
      imagesPayload,
      currentImageIndexToLoad,
      firstImagePayloadBatchCount,
      swiperModules: [Pagination]
    }
  },
  props: {
    advisers: {
      type: Array as PropType<Adviser[]>,
      required: true
    },
    selectedAdviser: {
      type: Object as PropType<Adviser | null>,
      required: false
    },
    changeAdviser: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    mappedAdvisers() {
      return this.advisers
    },
    advisersCount() {
      return this.mappedAdvisers.length
    },
    hasSwiperEnabled() {
      // return this.advisersCount > 4
      return true
    },
    showPagination() {
      return this.advisersCount > 3
    },
    availableAdvisersTitle() {
      return wTransChoice(
        'appointment_form_page.available_advisers.carousel.available_advisers',
        this.advisersCount,
        { count: this.advisersCount }
      )
    },
    lang(): string {
      return useSubmissionFormStore().formLanguage
    }
  },
  created() {
    this.advisers.forEach((adviser, index) => {
      this.imagesPayload.set(adviser.id, {
        id: adviser.id,
        index: index,
        url: adviser.photo.url,
        width: adviser.photo.width,
        height: adviser.photo.height,
        alt: adviser.name,
        ready: false,
        show: false
      })
    })
  },
  watch: {
    advisers() {
      this.imagesPayload.clear()
      this.advisers.forEach((adviser, index) => {
        this.imagesPayload.set(adviser.id, {
          id: adviser.id,
          index: index,
          url: adviser.photo.url,
          width: adviser.photo.width,
          height: adviser.photo.height,
          alt: adviser.name,
          ready: false,
          show: false
        })
      })
    }
  },
  methods: {
    selectAdviser(adviser: Adviser) {
      this.$emit('selectAdviser', adviser)
      const swiperEl = this.swiperRef
      if (!swiperEl) {
        return
      }
      swiperEl.swiper.slideTo(swiperEl.swiper.clickedIndex, 500, true, false)
    }
  }
})
</script>
